import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    selectCurrentOperation,
    selectOpen,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { fetchAclConditions } from 'common/services/api/Acls/AclConditions.thunk';
import { APIAclResourcePostParams, APIAclResourcePutParams } from 'common/services/api/Acls/AclResources.api';
import {
    postAclResource,
    PostAclResourceThunkResponse,
    putAclResource,
    PutAclResourceThunkResponse
} from 'common/services/api/Acls/AclResources.thunk';
import { AclCondition } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { DEFAULT_FIELD_STYLE, editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';
import { selectAclConditions, selectAclConditionsLoaded } from 'features/aclConditions/aclConditionsSelectors';
import { useSnackbar } from 'notistack';

import { AclResourcesConditionsMultiSelect } from './AclResourcesConditionsMultiSelect';

interface AclResourceItem {
    name: string;
    displayName: string;
    conditions?: string[];
}

interface AclResourceDrawerProperties {
    aclResourceNameFieldRef: React.RefObject<HTMLInputElement | null>;
    aclResourceDisplayNameFieldRef: React.RefObject<HTMLInputElement | null>;
    tenantName: string;
    tenantId: string;
    availableConditions: AclCondition[];
    availableConditionsLoaded: boolean;
    selectedConditions: string[];
    setSelectedConditions: (conditions: string[]) => void;
}

function useAclResourceDrawer(
    entityName: string,
    drawerProps: AclResourceDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<AclResourceItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();

    const drawerContext = useDrawerContext<
        AclResourceItem,
        AclResourceDrawerProperties,
        APIAclResourcePostParams,
        PostAclResourceThunkResponse,
        APIAclResourcePutParams,
        PutAclResourceThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const handleSaveBtnEnabled = useCallback(() => {
        dispatch(setSaveButtonEnabled(
            !!(drawerProps.aclResourceNameFieldRef?.current?.value && drawerProps.availableConditionsLoaded)
        ));
    }, [dispatch, drawerProps.aclResourceNameFieldRef, drawerProps.availableConditionsLoaded]);

    const getPostOptions = useCallback((): APIAclResourcePostParams => ({
        tenantId: drawerProps.tenantId,
        name: drawerProps.aclResourceNameFieldRef.current?.value ?? '',
        displayName: drawerProps.aclResourceDisplayNameFieldRef.current?.value || undefined,
        conditions: drawerProps.selectedConditions
    }), [drawerProps]);

    const getPutOptions = useCallback((): APIAclResourcePutParams | null => {
        if (!editingItemInfo?.editingItemId) {
            return null;
        }
        return {
            tenantId: drawerProps.tenantId,
            id: editingItemInfo?.editingItemId as string,
            name: drawerProps.aclResourceNameFieldRef.current?.value ?? '',
            displayName: drawerProps.aclResourceDisplayNameFieldRef.current?.value || null,
            conditions: drawerProps.selectedConditions
        };
    }, [drawerProps, editingItemInfo?.editingItemId]);

    const createSuccessHandler = useCallback((postResult: PostAclResourceThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setRows(postResult.rows.map(row => ({ ...row, conditionsDisplay: row.conditions?.join(', ') ?? '' }))));
    }, [drawerContext, dispatch]);

    const editSuccessHandler = useCallback((putResult: PutAclResourceThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows.map(row => ({ ...row, conditionsDisplay: row.conditions?.join(', ') ?? '' }))));
    }, [drawerContext, dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postAclResource, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putAclResource, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    return {
        ...drawerContext,
        handleSaveBtnEnabled,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    handleSaveBtnEnabled,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled: () => void;
    postCallback: () => void;
    drawerProps: AclResourceDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.aclResourceNameFieldRef}
                    size="small"
                    label="Resource Name"
                    placeholder="Type Resource Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                />
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.aclResourceDisplayNameFieldRef}
                    size="small"
                    label="Resource Display Name"
                    placeholder="Type Resource Display Name"
                    variant="filled"
                />
                <AclResourcesConditionsMultiSelect
                    availableConditions={drawerProps.availableConditions}
                    selectedConditions={drawerProps.selectedConditions}
                    onChange={drawerProps.setSelectedConditions}
                />
                <TextField
                    sx={DEFAULT_FIELD_STYLE}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={drawerProps.tenantName}
                    disabled
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={{
                            color: 'error.main',
                            marginTop: '20px !important',
                            fontSize: '12px'
                        }}>
                    ACL Resource name or display name already in use
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    nameInConflict,
    handleSaveBtnEnabled,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled: () => void;
    putCallback: () => void;
    drawerProps: AclResourceDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<AclResourceItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.aclResourceNameFieldRef}
                    size="small"
                    label="Resource Name"
                    defaultValue={editingItemInfo?.editingRow?.name}
                    placeholder="Type Resource Name"
                    variant="filled"
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                    focused
                />
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.aclResourceDisplayNameFieldRef}
                    size="small"
                    label="Resource Display Name"
                    defaultValue={editingItemInfo?.editingRow?.displayName}
                    placeholder="Type Resource Display Name"
                    variant="filled"
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                    focused
                />
                <AclResourcesConditionsMultiSelect
                    availableConditions={drawerProps.availableConditions}
                    selectedConditions={drawerProps.selectedConditions}
                    onChange={drawerProps.setSelectedConditions}
                    nameInConflict={nameInConflict}
                />
                <TextField
                    sx={DEFAULT_FIELD_STYLE}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={drawerProps.tenantName}
                    disabled
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={{
                            color: 'error.main',
                            marginTop: '20px !important',
                            fontSize: '12px'
                        }}>
                        ACL Resource name or display name already in use or a Condition was added and an Entry exists
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

export function AclResourcesCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<AclResourceItem>>) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const drawerOpen = useAppSelector(selectOpen);
    const currentOperation = useAppSelector(selectCurrentOperation);

    // Retrieve available ACL condition types
    const availableConditions = useAppSelector(selectAclConditions);
    const availableConditionsLoaded = useAppSelector(selectAclConditionsLoaded);

    useEffect(() => {
        if (drawerOpen && availableConditionsLoaded === false) {
            dispatch(setSaveButtonEnabled(false));
            dispatch(fetchAclConditions({ key: '' }))
                .unwrap()
                .then(() => dispatch(setSaveButtonEnabled(true)))
                .catch(() => {
                    dispatch(setSaveButtonEnabled(false));
                    enqueueSnackbar('There was an error while retrieving available ACL Condition types.' + DEFAULT_ERROR_MESSAGE, { variant: 'error' });
                });
        }
    }, [dispatch, drawerOpen, availableConditionsLoaded, enqueueSnackbar]);

    // Set/Reset selected conditions when opening drawer or switching operations
    const [selectedConditions, setSelectedConditions] = useState<string[]>([]);

    useEffect(() => {
        if (drawerOpen) {
            setSelectedConditions(currentOperation === Operations.Create ? [] : editingItemInfo?.editingRow?.conditions ?? []);
        }
    }, [editingItemInfo?.editingRow, currentOperation, drawerOpen]);

    const tenantDetails = useAppSelector(selectPageInfo);
    const aclResourceDrawerProps: AclResourceDrawerProperties = {
        aclResourceNameFieldRef: useRef<HTMLInputElement>(null),
        aclResourceDisplayNameFieldRef: useRef<HTMLInputElement>(null),
        tenantId: tenantDetails?.id ?? '',
        tenantName: tenantDetails?.name ?? '',
        availableConditions,
        availableConditionsLoaded,
        selectedConditions,
        setSelectedConditions
    };

    const drawerState = useAclResourceDrawer(title, aclResourceDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                postCallback={drawerState.postCallback}
                drawerProps={aclResourceDrawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                putCallback={drawerState.putCallback}
                drawerProps={aclResourceDrawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
